import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";

import { getObjectFilters } from "src/components/filters/queryFilterUtils";
import { ApiError } from "src/core/apiError";
import { MessageManager } from "src/core/streaming/core/messageManager";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import { ResultFormat } from "src/gen/schema/bilge/api";
import {
  CommandOutputRecord,
  CommandStatus
} from "src/gen/schema/schema/schema";
import { SchemaType } from "src/gen/schema/schema_path/schema_path";
import { FilterOp, IndexFilter } from "src/gen/schema/syncfollow/syncfollow";
import { Sextant } from "src/utils/api";

import {
  addCommandOutput,
  CommandHistory,
  MAX_OUTPUT_LINES
} from "../mockShellSessionManager";
import { getEmptyStateText } from "../mockShellUtils";
interface UseStreamingSubscriptionParams {
  readonly fabricId: string | undefined;
  readonly deviceId: string;
  readonly previousCommand: string;
  readonly executedAt: Date | undefined;
  readonly onError: (error: ApiError | undefined) => void;
  readonly onCommandHistoryUpdate: Dispatch<SetStateAction<CommandHistory>>;
  readonly selectedDeviceUnavailable?: boolean;
}
interface UseStreamingSubscriptionResult {
  readonly streamingOutput: string;
  readonly isStreamingActive: boolean;
  readonly startSession: (sessionId: string) => void;
  readonly handleStopCommand: () => Promise<void>;
}
/**
 * Custom hook to manage streaming command output subscriptions.
 * Handles real-time output updates, command status tracking, and cleanup.
 *
 * @param fabricId - The fabric ID to subscribe to
 * @param deviceId - The device ID to filter subscriptions
 * @param previousCommand - The command being executed
 * @param executedAt - Timestamp when command was executed
 * @param onError - Error callback for subscription errors
 * @param onCommandHistoryUpdate - Callback to update command history
 * @param selectedDeviceUnavailable - Check if the device is missing
 * @returns Streaming state and control functions
 */

export const useStreamingSubscription = ({
  fabricId,
  deviceId,
  previousCommand,
  executedAt,
  onError,
  onCommandHistoryUpdate,
  selectedDeviceUnavailable = false
}: UseStreamingSubscriptionParams): UseStreamingSubscriptionResult => {
  const [terminalStatus, setTerminalStatus] = useState<CommandStatus>();
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>();
  const [streamingOutput, setStreamingOutput] = useState<string>("");
  const [isStreamingActive, setIsStreamingActive] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number | undefined>(
    undefined
  );
  const [receivedRecords, setReceivedRecords] = useState<number>(0);

  const startSession = useCallback((sessionId: string) => {
    setStreamingOutput("");
    setActiveSessionId(sessionId);
    setIsStreamingActive(true);
    setTotalRecords(undefined);
    setReceivedRecords(0);
    setTerminalStatus(undefined);
  }, []);

  const resetStreamingState = useCallback(() => {
    setStreamingOutput("");
    setActiveSessionId(undefined);
    setIsStreamingActive(false);
    setTotalRecords(undefined);
    setReceivedRecords(0);
    setTerminalStatus(undefined);
  }, []);

  const finishStreamingCommand = useCallback(
    (output: string, emptyStateText?: string) => {
      if (previousCommand) {
        onCommandHistoryUpdate((prev) =>
          addCommandOutput(
            prev,
            previousCommand,
            output,
            ResultFormat.TEXT,
            executedAt,
            deviceId,
            emptyStateText
          )
        );
      }

      resetStreamingState();
    },
    [
      deviceId,
      executedAt,
      onCommandHistoryUpdate,
      previousCommand,
      resetStreamingState
    ]
  );

  const handleSubscriptionError = useCallback(
    (err: ApiError | undefined) => {
      onError(err);
      resetStreamingState();
    },
    [onError, resetStreamingState]
  );

  useEffect(() => {
    if (activeSessionId && fabricId) {
      const indexFilter: IndexFilter[] = [
        {
          index: SchemaType.CommandOutputType,
          filterOp: FilterOp.EQ,
          filterVal: activeSessionId
        }
      ];
      const unsubscribe = [
        Sextant.subscribeCommandOutputRecords(
          fabricId,
          [deviceId],
          getObjectFilters([], indexFilter, []),
          (records: CommandOutputRecord[]) => {
            const sorted = [...records].sort((a, b) => {
              const aId = a.id?.commandOutputRecord ?? 0;
              const bId = b.id?.commandOutputRecord ?? 0;
              return aId - bId;
            });
            const fullOutput = sorted.map((r) => r.output);
            const trimmed =
              fullOutput.length > MAX_OUTPUT_LINES
                ? fullOutput.slice(-MAX_OUTPUT_LINES).join("\n")
                : fullOutput.join("\n");
            setStreamingOutput(trimmed);
            setReceivedRecords(sorted.length);
          },
          handleSubscriptionError
        ),
        Sextant.subscribeCommandOutput(
          fabricId,
          [deviceId],
          getObjectFilters([], indexFilter, []),
          (outputs) => {
            const status = outputs[0]?.status;
            if (
              status === CommandStatus.COMMAND_STATUS_COMPLETED ||
              status === CommandStatus.COMMAND_STATUS_FAILED ||
              status === CommandStatus.COMMAND_STATUS_EXPIRED ||
              status === CommandStatus.COMMAND_STATUS_KILLED
            ) {
              setTerminalStatus(status);
              setTotalRecords(outputs[0]?.totalRecords ?? 0);
              const error = outputs[0]?.error;
              const isErrorStatus =
                status === CommandStatus.COMMAND_STATUS_FAILED ||
                status === CommandStatus.COMMAND_STATUS_EXPIRED;
              if (isErrorStatus && error) {
                onError(ApiError.fromMessage(error));
              }
            }
          },
          handleSubscriptionError
        )
      ];
      return () => {
        unsubscribe.forEach((cb) => cb());
      };
    }
    return MessageManager.unsubscribe;
  }, [activeSessionId, fabricId, deviceId, onError, handleSubscriptionError]);

  // move streaming output to history when all records received
  useEffect(() => {
    const hasCommandCompleted = totalRecords !== undefined;
    const hasReceivedAllRecords =
      totalRecords === 0 || receivedRecords === totalRecords;

    if (!hasCommandCompleted || !hasReceivedAllRecords) {
      return;
    }
    finishStreamingCommand(
      streamingOutput,
      getEmptyStateText(totalRecords, streamingOutput, terminalStatus)
    );
  }, [
    totalRecords,
    receivedRecords,
    finishStreamingCommand,
    terminalStatus,
    streamingOutput
  ]);

  const handleStopCommand = useCallback((): Promise<void> => {
    if (!activeSessionId || !deviceId) {
      return Promise.resolve();
    }
    return Bilge.stopTCliCommand({
      deviceId,
      sessionId: activeSessionId
    })
      .then(() => {
        return undefined;
      })
      .catch((err) => {
        onError(err);
        resetStreamingState();
      });
  }, [activeSessionId, deviceId, onError, resetStreamingState]);

  useEffect(() => {
    // If a streaming command is active and the selected device becomes unavailable,
    // backend may not publish a terminal command status. Finalize locally.
    if (!isStreamingActive || !activeSessionId || !selectedDeviceUnavailable) {
      return;
    }
    const message =
      "Command stopped because the scoped device is no longer available.";

    void handleStopCommand().finally(() => {
      finishStreamingCommand(streamingOutput, message);
    });
  }, [
    activeSessionId,
    finishStreamingCommand,
    selectedDeviceUnavailable,
    isStreamingActive,
    handleStopCommand,
    streamingOutput
  ]);

  return {
    streamingOutput,
    isStreamingActive,
    startSession,
    handleStopCommand
  };
};
